import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  colorChipEstadoChequeTercero,
  esChequeElectronico,
  etiquetaEstadoChequeTercero,
  esChequeRechazable,
  esChequeTerceroRechazado,
  etiquetaSucursalChequeTercero,
  etiquetaTipoSoporteCheque,
  resolverEstadoChequeTercero,
} from '../../utils/chequeTerceroEstado';
import {
  formatClienteCheque,
  formatCuentaDepositoCheque,
  formatProveedorCheque,
} from '../../utils/chequeTerceroDisplay';
import ChequeTerceroRechazoDialog from './ChequeTerceroRechazoDialog';
import ChequeTerceroRechazoDocumentosSection from './ChequeTerceroRechazoDocumentosSection';
import BcraCentralDeudoresDialog from '../shared/BcraCentralDeudoresDialog';
import type { ChequeBcraIngreso } from '../../types/bcraCentralDeudores';
import { bcraIngresoToConsulta } from '../../types/bcraCentralDeudores';
import { etiquetaNivelRiesgo } from '../../utils/bcraCentralDeudores';
import { formatNroCheque, formatNroChequeVista } from '../../utils/nroCheque';
import NroChequeTextField from './NroChequeTextField';
import { getAccessToken } from '../../utils/sessionAuthStore';

/** Datos mínimos comunes a los informes de cheques de tercero / cartera */
export interface ChequeTerceroDetalleData {
  nroCarga: number;
  nroBanco: number;
  nombreBanco?: string;
  nroSucursal: number;
  nombreSucursal?: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number | null;
  nroDestino: number | null;
  fechaSalida: string | null;
  nroMovCajaSalida: number | null;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
  senial?: number;
  rechazado?: number;
  fechaRechazo?: string | null;
  nroCliente?: number | null;
  nombreCliente?: string | null;
  nroProveedor?: number | null;
  nombreProveedor?: string | null;
  nroBancoDeposito?: number | null;
  nombreBancoDeposito?: string | null;
  nroCuentaDeposito?: number | null;
  bcraIngreso?: ChequeBcraIngreso | null;
  esElectronico?: boolean;
}

interface ChequeTerceroDetalleDialogProps {
  open: boolean;
  onClose: () => void;
  cheque: ChequeTerceroDetalleData | null;
  /** Tras guardar correctamente (solo SUPERADMIN) */
  onSaved?: () => void;
}

const toDateInput = (s: string | null | undefined): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const numToStr = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(date);
  } catch {
    return dateString;
  }
};

const bcraChipColor = (nivel: ChequeBcraIngreso['nivelRiesgo']) => {
  switch (nivel) {
    case 'riesgo':
      return 'error';
    case 'atencion':
      return 'warning';
    case 'normal':
      return 'success';
    default:
      return 'info';
  }
};

const ChequeTerceroDetalleDialog: React.FC<ChequeTerceroDetalleDialogProps> = ({
  open,
  onClose,
  cheque,
  onSaved
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isSuperAdmin = Boolean(user?.roles?.includes('SUPERADMIN'));

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [rechazoDocsReload, setRechazoDocsReload] = useState(0);
  const [bcraModalOpen, setBcraModalOpen] = useState(false);

  const syncFormFromCheque = useCallback((c: ChequeTerceroDetalleData) => {
    setForm({
      nroBanco: String(c.nroBanco),
      nroSucursal: String(c.nroSucursal),
      nroCheque: formatNroCheque(c.nroCheque),
      nroLibrador: (c.nroLibrador ?? '').trim(),
      librador: (c.librador ?? '').trim(),
      importe: String(c.importe ?? 0),
      fechaEmision: toDateInput(c.fechaEmision),
      fechaVencimiento: toDateInput(c.fechaVencimiento),
      fechaIngreso: toDateInput(c.fechaIngreso),
      fechaSalida: toDateInput(c.fechaSalida ?? undefined),
      nroMovCajaIngre: numToStr(c.nroMovCajaIngre),
      nroMovCajaSalida: numToStr(c.nroMovCajaSalida),
      nroDestino: numToStr(c.nroDestino),
      concepto: (c.concepto ?? '').trim(),
      mcp: (c.mcp ?? '').trim(),
      nroCuenta: (c.nroCuenta ?? '').trim(),
      conciliado: String(c.conciliado ?? 0),
      asentado: String(c.asentado ?? 0),
      senial: c.senial !== undefined && c.senial !== null ? String(c.senial) : ''
    });
  }, []);

  useEffect(() => {
    if (open && cheque) syncFormFromCheque(cheque);
  }, [open, cheque, syncFormFromCheque]);

  const getEstadoChip = (c: ChequeTerceroDetalleData) => {
    const estado = resolverEstadoChequeTercero(c);
    return (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        <Chip
          label={etiquetaTipoSoporteCheque(c)}
          color={esChequeElectronico(c) ? 'secondary' : 'default'}
          size="small"
          variant={esChequeElectronico(c) ? 'filled' : 'outlined'}
        />
        <Chip
          label={etiquetaEstadoChequeTercero(estado)}
          color={colorChipEstadoChequeTercero(estado)}
          size="small"
        />
      </Stack>
    );
  };

  const parseNullableInt = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
  };

  const parseNullableByte = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    if (Number.isNaN(n) || n < 0 || n > 255) return null;
    return n;
  };

  const buildIsoDate = (yyyyMmDd: string): string | null => {
    if (!yyyyMmDd || !yyyyMmDd.trim()) return null;
    const d = new Date(`${yyyyMmDd.trim()}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const handleSave = async () => {
    if (!cheque || !isSuperAdmin) return;
    setSaving(true);
    try {
      const token = getAccessToken();
      const imp = parseFloat(String(form.importe ?? '').replace(',', '.'));
      if (Number.isNaN(imp)) {
        enqueueSnackbar('Importe inválido.', { variant: 'error' });
        return;
      }
      const body = {
        nroBanco: parseInt(form.nroBanco, 10),
        nroSucursal: parseInt(form.nroSucursal, 10),
        nroCheque: parseInt(form.nroCheque, 10),
        nroLibrador: form.nroLibrador ?? '',
        librador: form.librador ?? '',
        importe: imp,
        fechaEmision: buildIsoDate(form.fechaEmision ?? ''),
        fechaVencimiento: buildIsoDate(form.fechaVencimiento ?? ''),
        fechaIngreso: buildIsoDate(form.fechaIngreso ?? ''),
        fechaSalida: buildIsoDate(form.fechaSalida ?? ''),
        nroMovCajaIngre: parseNullableInt(form.nroMovCajaIngre ?? ''),
        nroMovCajaSalida: parseNullableInt(form.nroMovCajaSalida ?? ''),
        nroDestino: parseNullableInt(form.nroDestino ?? ''),
        concepto: form.concepto ?? '',
        mcp: form.mcp ?? '',
        nroCuenta: form.nroCuenta ?? '',
        conciliado: parseNullableByte(form.conciliado ?? ''),
        asentado: parseNullableByte(form.asentado ?? ''),
        senial: parseNullableByte(form.senial ?? '')
      };

      if ([body.nroBanco, body.nroSucursal, body.nroCheque].some((x) => Number.isNaN(x))) {
        enqueueSnackbar('Banco, sucursal o nº de cheque inválidos.', { variant: 'error' });
        return;
      }

      await api.put(`/api/Tesoreria/cheques-terceros/${cheque.nroCarga}`, body, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      enqueueSnackbar('Cheque actualizado correctamente.', { variant: 'success' });
      onSaved?.();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ??
        err.response?.data?.title ??
        err.message ??
        'No se pudo guardar el cheque.';
      enqueueSnackbar(typeof msg === 'string' ? msg : 'Error al guardar.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleField = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  if (!cheque) return null;

  const bcraConsulta = bcraIngresoToConsulta(cheque.bcraIngreso);

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalle del Cheque{isSuperAdmin ? ' (edición)' : ''}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 0 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Carga</Typography>
            <Typography variant="body1">{cheque.nroCarga || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Cheque</Typography>
            {isSuperAdmin ? (
              <NroChequeTextField fullWidth size="small" value={form.nroCheque ?? ''} onChange={(nroCheque) => setForm((prev) => ({ ...prev, nroCheque }))} />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatNroChequeVista(cheque.nroCheque)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Banco (código)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroBanco ?? ''} onChange={handleField('nroBanco')} />
            ) : (
              <Typography variant="body1">{cheque.nombreBanco?.trim() || '-'}</Typography>
            )}
          </Grid>
          {!esChequeElectronico(cheque) && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Sucursal</Typography>
              {isSuperAdmin ? (
                <TextField fullWidth size="small" value={form.nroSucursal ?? ''} onChange={handleField('nroSucursal')} />
              ) : (
                <Typography variant="body1">{etiquetaSucursalChequeTercero(cheque.nroSucursal, cheque.nombreSucursal, '-', cheque.esElectronico)}</Typography>
              )}
            </Grid>
          )}
          {!isSuperAdmin && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Nombre banco</Typography>
                <Typography variant="body1">{cheque.nombreBanco?.trim() || '-'}</Typography>
              </Grid>
              {!esChequeElectronico(cheque) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Nombre sucursal</Typography>
                  <Typography variant="body1">{etiquetaSucursalChequeTercero(cheque.nroSucursal, cheque.nombreSucursal, '-', cheque.esElectronico)}</Typography>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Librador</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.librador ?? ''} onChange={handleField('librador')} />
            ) : (
              <Typography variant="body1">{cheque.librador?.trim() || cheque.nroLibrador?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">CUIT librador</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroLibrador ?? ''} onChange={handleField('nroLibrador')} />
            ) : (
              <Typography variant="body1">{cheque.nroLibrador?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Importe</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.importe ?? ''} onChange={handleField('importe')} />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatCurrency(cheque.importe || 0)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Cuenta</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroCuenta ?? ''} onChange={handleField('nroCuenta')} />
            ) : (
              <Typography variant="body1">{cheque.nroCuenta?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Emisión</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaEmision ?? ''} onChange={handleField('fechaEmision')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaEmision)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Vencimiento</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaVencimiento ?? ''} onChange={handleField('fechaVencimiento')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaVencimiento)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Ingreso</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaIngreso ?? ''} onChange={handleField('fechaIngreso')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaIngreso)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Salida</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaSalida ?? ''} onChange={handleField('fechaSalida')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaSalida || '')}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Mov. Caja Ingreso</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroMovCajaIngre ?? ''} onChange={handleField('nroMovCajaIngre')} />
            ) : (
              <Typography variant="body1">{cheque.nroMovCajaIngre ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Mov. Caja Salida</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroMovCajaSalida ?? ''} onChange={handleField('nroMovCajaSalida')} />
            ) : (
              <Typography variant="body1">{cheque.nroMovCajaSalida ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Comprobante Ingreso</Typography>
            <Typography variant="body1">{cheque.comprobanteIngreso?.trim() || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Comprobante Salida</Typography>
            <Typography variant="body1">{cheque.comprobanteSalida?.trim() || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Cliente</Typography>
            <Typography variant="body1">{formatClienteCheque(cheque)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Proveedor</Typography>
            <Typography variant="body1">{formatProveedorCheque(cheque)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Depósito bancario</Typography>
            <Typography variant="body1">{formatCuentaDepositoCheque(cheque)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Destino (Caja)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroDestino ?? ''} onChange={handleField('nroDestino')} />
            ) : (
              <Typography variant="body1">{cheque.nroDestino ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">MCP</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.mcp ?? ''} onChange={handleField('mcp')} />
            ) : (
              <Typography variant="body1">{cheque.mcp?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Estado</Typography>
            <Box sx={{ mt: 0.5 }}>{getEstadoChip(cheque)}</Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Conciliado (0/1)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.conciliado ?? ''} onChange={handleField('conciliado')} />
            ) : (
              <Typography variant="body1">{cheque.conciliado ? 'Sí' : 'No'}</Typography>
            )}
          </Grid>
          {isSuperAdmin && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Asentado (0/1)</Typography>
                <TextField fullWidth size="small" value={form.asentado ?? ''} onChange={handleField('asentado')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Señal</Typography>
                <TextField fullWidth size="small" value={form.senial ?? ''} onChange={handleField('senial')} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Concepto</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth multiline minRows={2} size="small" value={form.concepto ?? ''} onChange={handleField('concepto')} />
            ) : (
              <Typography variant="body1">{cheque.concepto?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Situación crediticia al ingreso (BCRA)
            </Typography>
            {cheque.bcraIngreso?.nivelRiesgo ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={etiquetaNivelRiesgo(cheque.bcraIngreso.nivelRiesgo)}
                  color={bcraChipColor(cheque.bcraIngreso.nivelRiesgo)}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  {cheque.bcraIngreso.mensajeResumen}
                </Typography>
                {cheque.bcraIngreso.fechaConsulta && (
                  <Typography variant="caption" color="text.secondary">
                    Consultado: {formatDate(cheque.bcraIngreso.fechaConsulta)}
                  </Typography>
                )}
                {bcraConsulta && (
                  <Button size="small" variant="text" onClick={() => setBcraModalOpen(true)}>
                    Ver informe
                  </Button>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No registrada al ingreso
              </Typography>
            )}
          </Grid>
          {esChequeTerceroRechazado(cheque) && (
            <Grid item xs={12}>
              <ChequeTerceroRechazoDocumentosSection
                nroCarga={cheque.nroCarga}
                fechaRechazo={cheque.fechaRechazo}
                reloadToken={rechazoDocsReload}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cerrar</Button>
        {cheque && esChequeRechazable(cheque) && (
          <Button
            variant="outlined"
            color="error"
            disabled={saving}
            onClick={() => setRechazoOpen(true)}
          >
            Rechazar cheque
          </Button>
        )}
        {isSuperAdmin && (
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}>
            Guardar
          </Button>
        )}
      </DialogActions>
    </Dialog>
    <ChequeTerceroRechazoDialog
      open={rechazoOpen}
      nroCarga={cheque?.nroCarga ?? null}
      onClose={() => setRechazoOpen(false)}
      onSuccess={() => {
        setRechazoOpen(false);
        setRechazoDocsReload((t) => t + 1);
        onSaved?.();
        onClose();
      }}
    />
    <BcraCentralDeudoresDialog
      open={bcraModalOpen}
      onClose={() => setBcraModalOpen(false)}
      data={bcraConsulta}
    />
    </>
  );
};

export default ChequeTerceroDetalleDialog;
